/**
 * Sticky Add to Cart functionality
 * Shows sticky button when product form buttons reach header height
 * Hides when scrolling back to product info section
 */

class StickyAddToCart {
  constructor() {
    this.stickyButton = document.querySelector('.skb-sticky-add-to-cart');
    this.productFormButtons = document.querySelector('.skb-product-form__buttons');
    
    if (!this.stickyButton || !this.productFormButtons) {
      return;
    }

    this.headerHeight = this.getHeaderHeight();
    this.isVisible = false;
    
    this.init();
    this.initVariantChangeListener();
    this.initQuantitySync();
  }

  getHeaderHeight() {
    const root = document.documentElement;
    const headerHeight = getComputedStyle(root).getPropertyValue('--header-height');
    
    // Parse the value (could be in px, rem, etc.)
    if (headerHeight.includes('px')) {
      return parseFloat(headerHeight);
    } else if (headerHeight.includes('rem')) {
      return parseFloat(headerHeight) * parseFloat(getComputedStyle(root).fontSize);
    }
    
    // Default fallback
    return 80;
  }

  init() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', () => {
      this.headerHeight = this.getHeaderHeight();
      this.handleScroll();
    }, { passive: true });
    
    // Initial check
    this.handleScroll();
  }

  initVariantChangeListener() {
    // Listen for variant changes to sync button and quantity state
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        if (this.isVisible) {
          this.syncButtonState();
        }
      });
    }
  }

  handleScroll() {
    if (!this.productFormButtons) return;

    const productFormButtonsRect = this.productFormButtons.getBoundingClientRect();

    // Check if product form buttons have reached or passed the header height
    const hasReachedHeaderHeight = productFormButtonsRect.top <= this.headerHeight;

    // Show sticky button when product form buttons cross the top (reach header height)
    // Hide when scrolling back up and product form buttons are visible again
    if (hasReachedHeaderHeight) {
      if (!this.isVisible) {
        this.show();
      }
    } else {
      if (this.isVisible) {
        this.hide();
      }
    }
  }

  show() {
    if (this.stickyButton) {
      this.stickyButton.style.display = 'block';
      this.isVisible = true;
      
      // Sync button and quantity state with original
      this.syncButtonState();
    }
  }

  hide() {
    if (this.stickyButton) {
      this.stickyButton.style.display = 'none';
      this.isVisible = false;
    }
  }

  syncButtonState() {
    const originalButton = document.querySelector('.skb-product-form__buttons .product-form__submit');
    const stickyButton = this.stickyButton.querySelector('.skb-sticky-add-to-cart__button');
    
    if (originalButton && stickyButton) {
      // Sync disabled state
      stickyButton.disabled = originalButton.disabled || originalButton.hasAttribute('aria-disabled');
      
      // Sync button text
      const originalText = originalButton.querySelector('span');
      const stickyText = stickyButton.querySelector('span');
      if (originalText && stickyText) {
        stickyText.textContent = originalText.textContent.trim();
      }
    }
    
    // Sync quantity input
    this.syncQuantityInput();
  }

  syncQuantityInput() {
    const originalQuantityInput = document.querySelector('.skb-product-form__buttons .quantity__input');
    const stickyQuantityInput = this.stickyButton.querySelector('.quantity__input');
    
    if (originalQuantityInput && stickyQuantityInput) {
      // Sync value
      stickyQuantityInput.value = originalQuantityInput.value;
      
      // Sync attributes
      stickyQuantityInput.min = originalQuantityInput.min;
      stickyQuantityInput.max = originalQuantityInput.max || '';
      stickyQuantityInput.step = originalQuantityInput.step;
      stickyQuantityInput.setAttribute('data-min', originalQuantityInput.getAttribute('data-min') || '');
      stickyQuantityInput.setAttribute('data-max', originalQuantityInput.getAttribute('data-max') || '');
      stickyQuantityInput.setAttribute('data-cart-quantity', originalQuantityInput.getAttribute('data-cart-quantity') || '0');
      
      // Sync disabled state of quantity buttons
      const originalMinusButton = originalQuantityInput.closest('quantity-input')?.querySelector('button[name="minus"]');
      const originalPlusButton = originalQuantityInput.closest('quantity-input')?.querySelector('button[name="plus"]');
      const stickyMinusButton = stickyQuantityInput.closest('quantity-input')?.querySelector('button[name="minus"]');
      const stickyPlusButton = stickyQuantityInput.closest('quantity-input')?.querySelector('button[name="plus"]');
      
      if (originalMinusButton && stickyMinusButton) {
        stickyMinusButton.disabled = originalMinusButton.disabled;
        stickyMinusButton.classList.toggle('disabled', originalMinusButton.classList.contains('disabled'));
      }
      
      if (originalPlusButton && stickyPlusButton) {
        stickyPlusButton.disabled = originalPlusButton.disabled;
        stickyPlusButton.classList.toggle('disabled', originalPlusButton.classList.contains('disabled'));
      }
    }
  }

  initQuantitySync() {
    // Sync quantity when original quantity changes
    const originalQuantityInput = document.querySelector('.skb-product-form__buttons .quantity__input');
    if (originalQuantityInput) {
      // Listen for input and change events
      originalQuantityInput.addEventListener('input', () => {
        if (this.isVisible) {
          this.syncQuantityInput();
        }
      });
      
      originalQuantityInput.addEventListener('change', () => {
        if (this.isVisible) {
          this.syncQuantityInput();
        }
      });
    }
    
    // Sync quantity when sticky quantity changes (update original)
    const stickyQuantityInput = this.stickyButton?.querySelector('.quantity__input');
    if (stickyQuantityInput) {
      stickyQuantityInput.addEventListener('input', () => {
        const originalQuantityInput = document.querySelector('.skb-product-form__buttons .quantity__input');
        if (originalQuantityInput) {
          originalQuantityInput.value = stickyQuantityInput.value;
          // Trigger change event on original to update any dependent elements
          originalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      
      stickyQuantityInput.addEventListener('change', () => {
        const originalQuantityInput = document.querySelector('.skb-product-form__buttons .quantity__input');
        if (originalQuantityInput) {
          originalQuantityInput.value = stickyQuantityInput.value;
          originalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
}

// Initialize when DOM is ready
let stickyAddToCartInstance = null;

function initStickyAddToCart() {
  if (!stickyAddToCartInstance) {
    stickyAddToCartInstance = new StickyAddToCart();
  }
  return stickyAddToCartInstance;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initStickyAddToCart();
  });
} else {
  initStickyAddToCart();
}

// Also sync button and quantity state when variant changes
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, () => {
    if (stickyAddToCartInstance && stickyAddToCartInstance.isVisible) {
      stickyAddToCartInstance.syncButtonState();
    }
  });
}


